import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogProps,
  Typography,
  Button,
  TextField,
  FormControl,
  Select,
  MenuItem,
} from '@material-ui/core';
import { toast } from 'react-toastify';
import {
  IoIosArrowDropdownCircle,
  IoIosCheckmarkCircleOutline,
} from 'react-icons/io';
import { useTranslation } from 'react-i18next';
import api from '../../../services/api';
import { useAuth } from '../../../hooks/auth';
import { useWindowSize } from '../../../hooks/windowSize';
import { ICreatedOrder, ICreateOrderResponse } from '../../../types/orders';
import { useStyles } from './styles';

interface ICreateOrderDialogProps extends DialogProps {
  unitId: string;
  lng: number;
  lat: number;
  road: string;
  region: string;
  onCancel?: any;
  onDone?: any;
}

const CreateOrderModal: React.FC<ICreateOrderDialogProps> = ({
  unitId,
  lat,
  lng,
  road,
  region,
  onCancel,
  onDone,
  ...rest
}) => {
  const classes = useStyles();
  const { jwt, user } = useAuth();
  const { t } = useTranslation();
  const [selectedPriority, setSelectedPriority] = useState<string | boolean>(
    false,
  );
  const [description, setDescription] = useState<string>('');
  const [createdOrder, setCreatedOrder] = useState<ICreatedOrder | null>(null);
  const size = useWindowSize();

  const handlePriorityChange = (event: any): void => {
    setSelectedPriority(event.target.value);
  };

  const handleCreateOrder = async (): Promise<void> => {
    const newOrderData = new FormData();

    if (selectedPriority === false) {
      toast.error(t('components:toast.error.selectPriority'));
      return;
    }

    newOrderData.append('priority', String(selectedPriority));
    newOrderData.append('description', description ? String(description) : '-');
    newOrderData.append('lat', String(lat));
    newOrderData.append('lng', String(lng));
    newOrderData.append('user_id', String(user.id));
    newOrderData.append('unit_id', String(unitId));

    try {
      const result = await api.post<ICreateOrderResponse>(
        '/orders',
        newOrderData,
        {
          headers: {
            'X-Access-Token': jwt,
          },
        },
      );
      setCreatedOrder(result.data.order);
    } catch {
      toast.error(t('components:toast.error.createOrderError'));
    }
  };

  useEffect(() => {
    if (createdOrder) {
      setTimeout(() => {
        setCreatedOrder(null);
        onDone();
      }, 5000);
    }
  }, [createdOrder, onDone]);

  return (
    <Dialog
      className={classes.container}
      aria-labelledby={t('components:utils.createOrderDialogAria')}
      aria-describedby={t('components:utils.createOrderDialogAriaDescribed')}
      {...rest}
    >
      <DialogContent
        style={{
          width: '100%',
          height: '100%',
        }}
      >
        {!createdOrder && (
          <>
            <div className={classes.infoContainer}>
              <div className={classes.info} style={{ marginRight: 8 }}>
                <Typography className={classes.infoText} variant="subtitle1">
                  {road === '-'
                    ? road
                    : `${t('components:utils.road')} ${road}`}
                </Typography>
              </div>
              <div className={classes.info}>
                <Typography className={classes.infoText} variant="subtitle1">
                  {region || '-'}
                </Typography>
              </div>
            </div>

            <div className={classes.inputContainer}>
              <Typography className={classes.inputLabel} variant="subtitle1">
                {t('components:utils.priority')}
              </Typography>
              <form className={classes.form} noValidate>
                <FormControl className={classes.formControl}>
                  <Select
                    autoFocus
                    value={selectedPriority}
                    onChange={handlePriorityChange}
                    variant="outlined"
                    IconComponent={props => (
                      <IoIosArrowDropdownCircle
                        size={24}
                        color="#7030A0"
                        style={{ marginLeft: 8 }}
                        {...props}
                      />
                    )}
                    className={classes.select}
                    style={{
                      width: size.width ? size.width * 0.3 : '100%',
                    }}
                  >
                    <MenuItem disabled value={false as any}>
                      {t('components:utils.selectPriority')}
                    </MenuItem>
                    <MenuItem value="BAIXA">
                      {t('components:utils.low')}
                    </MenuItem>
                    <MenuItem value="MEDIA">
                      {t('components:utils.average')}
                    </MenuItem>
                    <MenuItem value="ALTA">
                      {t('components:utils.high')}
                    </MenuItem>
                    <MenuItem value="URGENTE">
                      {t('components:utils.urgent')}
                    </MenuItem>
                  </Select>
                </FormControl>
              </form>
            </div>
            <div className={classes.inputContainer}>
              <Typography className={classes.inputLabel} variant="subtitle1">
                {t('components:utils.orderDescription')}
              </Typography>
              <TextField
                autoFocus
                id="order-description"
                type="text"
                placeholder={t('components:utils.includeOrderInformation')}
                fullWidth
                multiline
                variant="outlined"
                size="small"
                className={classes.description}
                inputProps={{ maxLength: 200 }}
                onChange={event => setDescription(event.target.value)}
              />
            </div>
          </>
        )}
        {createdOrder && (
          <div
            className={classes.confirmationContainer}
            style={{
              height: size.height ? size.height * 0.4 : '100%',
              width: size.height ? size.height * 0.4 : '100%',
            }}
          >
            <div className={classes.confirmationInfoContainer}>
              <Typography
                variant="subtitle1"
                className={classes.confirmationMessage}
              >
                {t('components:utils.orderCreated')}
              </Typography>
              <Typography
                variant="h3"
                className={classes.confirmationCode}
              >{`${t('components:utils.code')} ${
                createdOrder.code
              }`}</Typography>
            </div>
            <div className={classes.confirmationIconContainer}>
              <IoIosCheckmarkCircleOutline
                size={200}
                color="#2EB85C"
                style={{ fontWeight: 'bold' }}
              />
            </div>
            {/* <button
              type="button"
              onClick={onDone}
              className={classes.confirmationCloseButton}
            >
              <IoIosCloseCircleOutline size={24} />
            </button> */}
          </div>
        )}
      </DialogContent>
      {!createdOrder && (
        <DialogActions className={classes.buttonsContainer}>
          <Button
            variant="outlined"
            size="large"
            onClick={onCancel}
            color="primary"
            className={classes.button}
          >
            {t('components:buttons.cancel')}
          </Button>
          <Button
            variant="contained"
            size="large"
            onClick={() => handleCreateOrder()}
            color="primary"
            className={classes.button}
          >
            {t('components:buttons.save')}
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
};

export default CreateOrderModal;
